import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TableColumn } from '../../../models/shared/table-column.model';

/**
 * Componente reutilizável para seleção de colunas visíveis em tabelas
 * Persiste as preferências no localStorage
 */
@Component({
  selector: 'app-column-selector',
  templateUrl: './column-selector.component.html',
  styleUrls: ['./column-selector.component.scss'],
})
export class ColumnSelectorComponent implements OnInit {
  /** Array de configuração das colunas */
  @Input() columns: TableColumn[] = [];

  /** Chave para localStorage */
  @Input() storageKey: string = '';

  /** Emitido quando visibilidade muda */
  @Output() columnsChange = new EventEmitter<TableColumn[]>();

  ngOnInit(): void {
    this.loadFromStorage();
  }

  /** Quantidade de colunas visíveis */
  get visibleCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  /** Total de colunas */
  get totalCount(): number {
    return this.columns.length;
  }

  /**
   * Alterna visibilidade de uma coluna
   */
  onColumnToggle(column: TableColumn): void {
    // Colunas fixas não podem ser alteradas
    if (column.fixed) {
      return;
    }

    this.saveToStorage();
    this.columnsChange.emit([...this.columns]);
  }

  /**
   * Restaura visibilidade padrão
   */
  restore(): void {
    this.columns.forEach(col => {
      col.visible = col.defaultVisible;
    });
    this.saveToStorage();
    this.columnsChange.emit([...this.columns]);
  }

  /**
   * Carrega preferências do localStorage.
   * Colunas novas (que não existiam quando as preferências foram salvas)
   * respeitam seu defaultVisible em vez de ficarem ocultas.
   */
  private loadFromStorage(): void {
    if (!this.storageKey) {
      return;
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        // Detecta todas as chaves que existiam quando as preferências foram salvas
        const allStoredKeys = new Set(visibleKeys);
        // Verifica se há chaves ocultas salvas para determinar o conjunto completo
        const storedHidden = localStorage.getItem(this.storageKey + '-all');
        const allKnownKeys = storedHidden
          ? new Set(JSON.parse(storedHidden) as string[])
          : allStoredKeys;

        this.columns.forEach(col => {
          if (!col.fixed) {
            if (allKnownKeys.has(col.key)) {
              // Coluna existia quando as preferências foram salvas
              col.visible = visibleKeys.includes(col.key);
            } else {
              // Coluna nova: respeita defaultVisible
              col.visible = col.defaultVisible;
            }
          }
        });
        this.columnsChange.emit([...this.columns]);
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  /**
   * Salva preferências no localStorage.
   * Salva tanto as colunas visíveis quanto todas as chaves conhecidas,
   * para que colunas adicionadas futuramente sejam detectadas como novas.
   */
  private saveToStorage(): void {
    if (!this.storageKey) {
      return;
    }

    try {
      const visibleKeys = this.columns
        .filter(col => col.visible)
        .map(col => col.key);
      const allKeys = this.columns.map(col => col.key);
      localStorage.setItem(this.storageKey, JSON.stringify(visibleKeys));
      localStorage.setItem(this.storageKey + '-all', JSON.stringify(allKeys));
    } catch (e) {
      console.warn('Erro ao salvar preferências de colunas:', e);
    }
  }

  /**
   * Track function para ngFor
   */
  trackByKey(index: number, col: TableColumn): string {
    return col.key;
  }
}
